/**
 * FIRESTORE PATH CONSTANTS
 * Single Source of Truth for all database paths
 * 
 * @version 2.0.0
 * @description DO NOT HARDCODE PATHS ANYWHERE ELSE IN THE CODEBASE
 * 
 * SCHEMA STANDARD (as of Dec 2025):
 * - companyId is the ONLY company identifier field (slug format, e.g., "ac-oil-gas")
 * - No more companyId or companyId - REMOVED
 * - companyDisplayName stored ONLY on company document (for UI display)
 * 
 * ID FORMATS:
 * - companyId: slug ("ac-oil-gas")
 * - wellId: slug ("alpha-5")
 * - vendorId: slug ("smith-trucking")
 * - ticketId: sequential ("TKT-2025-00001")
 * - invoiceId: sequential ("INV-2025-00001")
 * - production entries: date ("20251223")
 * - photos/notes/memos: timestamp ("20251223-143052")
 * 
 * REQUIRED DOCUMENT FIELDS:
 * - id: document ID
 * - companyId: company slug
 * - createdAt: serverTimestamp()
 * - createdBy: user ID who created
 */

export const FIRESTORE_PATHS = {
  // ============================================
  // TENANT-SCOPED COLLECTIONS (Primary)
  // ============================================
  
  // Companies (Root)
  companies: 'companies',
  company: (companyId: string) => `companies/${companyId}`,
  
  // Users (ALWAYS under company)
  users: (companyId: string) => `companies/${companyId}/users`,
  user: (companyId: string, userId: string) => `companies/${companyId}/users/${userId}`,
  userPreferences: (companyId: string, userId: string) => `companies/${companyId}/users/${userId}/preferences/settings`,
  userSecurity: (companyId: string, userId: string) => `companies/${companyId}/users/${userId}/security/bio`,
  
  // Wells (ALWAYS under company)
  wells: (companyId: string) => `companies/${companyId}/wells`,
  well: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}`,
  
  // Well Subcollections (ALWAYS under well)
  wellNotes: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/notes`,
  wellNote: (companyId: string, wellId: string, noteId: string) => `companies/${companyId}/wells/${wellId}/notes/${noteId}`,
  
  wellPhotos: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/photos`,
  wellPhoto: (companyId: string, wellId: string, photoId: string) => `companies/${companyId}/wells/${wellId}/photos/${photoId}`,
  
  wellVoiceMemos: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/voice_memos`,
  wellVoiceMemo: (companyId: string, wellId: string, memoId: string) => `companies/${companyId}/wells/${wellId}/voice_memos/${memoId}`,
  
  // PRODUCTION ENTRIES - Document ID is YYYYMMDD format (e.g., "20251223")
  wellProductionEntries: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/production_entries`,
  wellProductionEntry: (companyId: string, wellId: string, dateId: string) => `companies/${companyId}/wells/${wellId}/production_entries/${dateId}`,
  
  wellDraftEntries: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/draft_entries`,
  wellDraftEntry: (companyId: string, wellId: string, draftId: string) => `companies/${companyId}/wells/${wellId}/draft_entries/${draftId}`,
  
  wellSatelliteViews: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/satellite_views`,
  wellSatelliteView: (companyId: string, wellId: string, viewId: string) => `companies/${companyId}/wells/${wellId}/satellite_views/${viewId}`,
  
  wellMaintenance: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/maintenance`,
  wellDowntime: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/downtime`,
  wellChemicalTreatments: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/chemical_treatments`,
  
  // Vendors (ALWAYS under company)
  vendors: (companyId: string) => `companies/${companyId}/vendors`,
  vendor: (companyId: string, vendorId: string) => `companies/${companyId}/vendors/${vendorId}`,
  vendorInvitations: (companyId: string) => `companies/${companyId}/vendor_invitations`,
  vendorInvitation: (companyId: string, invitationId: string) => `companies/${companyId}/vendor_invitations/${invitationId}`,
  
  // User Invitations (ALWAYS under company)
  userInvitations: (companyId: string) => `companies/${companyId}/user_invitations`,
  userInvitation: (companyId: string, invitationId: string) => `companies/${companyId}/user_invitations/${invitationId}`,
  
  // Tickets (ALWAYS under company)
  tickets: (companyId: string) => `companies/${companyId}/tickets`,
  ticket: (companyId: string, ticketId: string) => `companies/${companyId}/tickets/${ticketId}`,
  
  // Routes (ALWAYS under company)
  routes: (companyId: string) => `companies/${companyId}/routes`,
  route: (companyId: string, routeId: string) => `companies/${companyId}/routes/${routeId}`,
  
  // Alerts & Notifications (ALWAYS under company)
  alerts: (companyId: string) => `companies/${companyId}/alerts`,
  alert: (companyId: string, alertId: string) => `companies/${companyId}/alerts/${alertId}`,
  notifications: (companyId: string) => `companies/${companyId}/notifications`,
  notification: (companyId: string, notificationId: string) => `companies/${companyId}/notifications/${notificationId}`,
  notificationPreferences: (companyId: string) => `companies/${companyId}/notificationPreferences`,
  notificationPreference: (companyId: string, userId: string) => `companies/${companyId}/notificationPreferences/${userId}`,
  
  // Company Metadata (ALWAYS under company)
  metadata: (companyId: string) => `companies/${companyId}/metadata`,
  metadataDoc: (companyId: string, docId: string) => `companies/${companyId}/metadata/${docId}`,
  
  // Time Entries (ALWAYS under company)
  timeEntries: (companyId: string) => `companies/${companyId}/time_entries`,
  timeEntry: (companyId: string, entryId: string) => `companies/${companyId}/time_entries/${entryId}`,
  
  // Equipment (ALWAYS under company)
  equipment: (companyId: string) => `companies/${companyId}/equipment`,
  equipmentItem: (companyId: string, equipmentId: string) => `companies/${companyId}/equipment/${equipmentId}`,
  equipmentHistory: (companyId: string, equipmentId: string) => `companies/${companyId}/equipment/${equipmentId}/history`,
  
  // Parts Inventory (ALWAYS under company)
  parts: (companyId: string) => `companies/${companyId}/parts`,
  part: (companyId: string, partId: string) => `companies/${companyId}/parts/${partId}`,
  partTransactions: (companyId: string, partId: string) => `companies/${companyId}/parts/${partId}/transactions`,
  
  // Maintenance (ALWAYS under company)
  maintenance: (companyId: string) => `companies/${companyId}/maintenance`,
  maintenanceRecord: (companyId: string, maintenanceId: string) => `companies/${companyId}/maintenance/${maintenanceId}`,
  
  // Tasks (ALWAYS under company)
  tasks: (companyId: string) => `companies/${companyId}/tasks`,
  task: (companyId: string, taskId: string) => `companies/${companyId}/tasks/${taskId}`,
  
  // Messages (ALWAYS under company)
  messages: (companyId: string) => `companies/${companyId}/messages`,
  message: (companyId: string, messageId: string) => `companies/${companyId}/messages/${messageId}`,
  
  // Invoices (ALWAYS under company)
  invoices: (companyId: string) => `companies/${companyId}/invoices`,
  invoice: (companyId: string, invoiceId: string) => `companies/${companyId}/invoices/${invoiceId}`,
  
  // Hauling Records (ALWAYS under company)
  haulingRecords: (companyId: string) => `companies/${companyId}/hauling_records`,
  haulingRecord: (companyId: string, recordId: string) => `companies/${companyId}/hauling_records/${recordId}`,
  
  // Well Checks (ALWAYS under company)
  wellChecks: (companyId: string) => `companies/${companyId}/well_checks`,
  wellCheck: (companyId: string, checkId: string) => `companies/${companyId}/well_checks/${checkId}`,
  
  // Shared Routes (ALWAYS under company)
  sharedRoutes: (companyId: string) => `companies/${companyId}/shared_routes`,
  sharedRoute: (companyId: string, routeId: string) => `companies/${companyId}/shared_routes/${routeId}`,
  
  // Route Hazards (ALWAYS under company)
  routeHazards: (companyId: string) => `companies/${companyId}/route_hazards`,
  routeHazard: (companyId: string, hazardId: string) => `companies/${companyId}/route_hazards/${hazardId}`,
  
  // Shift Handoffs (ALWAYS under company)
  shiftHandoffs: (companyId: string) => `companies/${companyId}/shift_handoffs`,
  shiftHandoff: (companyId: string, handoffId: string) => `companies/${companyId}/shift_handoffs/${handoffId}`,
  
  // Audit Logs (ALWAYS under company)
  auditLogs: (companyId: string) => `companies/${companyId}/audit_logs`,
  auditLog: (companyId: string, logId: string) => `companies/${companyId}/audit_logs/${logId}`,
  
  // Sequential ID Counters (ALWAYS under company)
  counters: (companyId: string) => `companies/${companyId}/counters`,
  counter: (companyId: string, entityType: string) => `companies/${companyId}/counters/${entityType}`,
  
  // ============================================
  // WELL ASSIGNMENT PATHS (Tasks #18-22)
  // ============================================
  
  // Task #18: Well Visibility Settings (singleton per company)
  wellVisibilitySettings: (companyId: string) => 
    `companies/${companyId}/settings/wellVisibility`,
  
  // Company settings (generic)
  companySettings: (companyId: string) => `companies/${companyId}/settings`,
  companySetting: (companyId: string, settingId: string) => `companies/${companyId}/settings/${settingId}`,
  
  // Task #19: Assignment History (subcollection under wells)
  wellAssignmentHistory: (companyId: string, wellId: string) => 
    `companies/${companyId}/wells/${wellId}/assignment_history`,
  
  // Task #20: Single history entry
  wellAssignmentHistoryEntry: (companyId: string, wellId: string, historyId: string) => 
    `companies/${companyId}/wells/${wellId}/assignment_history/${historyId}`,
  
  // Task #21: Temporary Assignments (company-level collection)
  temporaryAssignments: (companyId: string) => 
    `companies/${companyId}/temporary_assignments`,
  
  // Task #22: Single temporary assignment
  temporaryAssignment: (companyId: string, assignmentId: string) => 
    `companies/${companyId}/temporary_assignments/${assignmentId}`,
  
  // ============================================
  // LOOKUP COLLECTIONS (For public access with tokens)
  // ============================================
  userInvitationLookups: 'user_invitation_lookups',
  userInvitationLookup: (phoneDigits: string) => `user_invitation_lookups/${phoneDigits}`,
  vendorInvitationLookups: 'vendor_invitation_lookups',
  vendorInvitationLookup: (phoneDigits: string) => `vendor_invitation_lookups/${phoneDigits}`,
  
  // ============================================
  // NAVIGATORX COLLECTIONS (Standalone App Users)
  // These users are NOT affiliated with any company
  // Goal: Upsell to full OilWellX platform
  // ============================================
  navUsers: 'nav_users',
  navUser: (userId: string) => `nav_users/${userId}`,
  navUserRoutes: (userId: string) => `nav_users/${userId}/routes`,
  navUserRoute: (userId: string, routeId: string) => `nav_users/${userId}/routes/${routeId}`,
  navUserFavorites: (userId: string) => `nav_users/${userId}/favorites`,
  navUserFavorite: (userId: string, favoriteId: string) => `nav_users/${userId}/favorites/${favoriteId}`,
  navInvitations: 'nav_invitations',
  navInvitation: (token: string) => `nav_invitations/${token}`,
  
  // ============================================
  // STORAGE PATHS (Firebase Storage)
  // ============================================
  storageCompany: (companyId: string) => `companies/${companyId}`,
  storageWellPhotos: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/photos`,
  storageWellVoiceMemos: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/voice-memos`,
  storageWellSatellite: (companyId: string, wellId: string) => `companies/${companyId}/wells/${wellId}/satellite`,
  storageUserProfile: (companyId: string, userId: string) => `companies/${companyId}/users/${userId}/profile`,
  storageTicketAttachments: (companyId: string, ticketId: string) => `companies/${companyId}/tickets/${ticketId}/attachments`,
  storageInvoices: (companyId: string) => `companies/${companyId}/invoices`,
} as const;

// Type for path keys
export type FirestorePathKey = keyof typeof FIRESTORE_PATHS;

// ============================================
// ID GENERATORS
// ============================================

/**
 * Generate a unique ID for assignment history entries
 * Format: {timestamp}_{action}_{random}
 * Example: 1703894400000_assigned_abc123
 */
export function generateHistoryEntryId(action: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${timestamp}_${action}_${random}`;
}

// Export as default too for convenience
export default FIRESTORE_PATHS;
